import { useQuery } from "@tanstack/react-query";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

import {
  labelForRow,
  type LedgerRow,
} from "./creditHistoryHelpers";


const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const CreditHistoryPanel = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["account-credit-history", user?.id],
    enabled: !!user?.id && open,
    queryFn: async (): Promise<LedgerRow[]> => {
      const { data, error } = await supabase
        .from("account_ai_credit_ledger")
        .select("id, kind, credits, balance_after, job_kind, created_at, metadata")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as LedgerRow[];
    },
  });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between -ml-2">
          <span className="text-xs">Credit history</span>
          <ChevronDown
            className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        {q.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : q.isError ? (
          <p className="text-xs text-muted-foreground">Couldn't load history.</p>
        ) : !q.data || q.data.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="space-y-1 text-xs max-h-64 overflow-y-auto">
            {q.data.map((row) => {
              const positive = row.credits > 0;
              const label = labelForRow(row);
              return (
                <div
                  key={row.id}
                  className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0"
                >
                  <div className="flex flex-col">
                    <span>{label}</span>
                    <span className="text-muted-foreground text-[10px]">
                      {fmtDate(row.created_at)}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className={positive ? "text-primary font-medium" : "font-medium"}>
                      {positive ? "+" : ""}{row.credits}
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      bal {row.balance_after}
                    </div>
                  </div>
                </div>
              );
            })}
            {q.data.length === 50 && (
              <p className="pt-1 text-[10px] text-muted-foreground">
                Showing the most recent 50 entries.
              </p>
            )}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
